import React, { useState } from "react";
import { X, Loader2, Check } from "lucide-react";
import { cancelSubscription } from "../lib/api";
import { toast } from "sonner";

export const CANCEL_REASONS = [
  { key: "too_expensive", label: "Está caro para mim" },
  { key: "unused", label: "Não uso com frequência" },
  { key: "missing_features", label: "Faltam recursos que preciso" },
  { key: "too_complex", label: "Achei difícil de usar" },
  { key: "switched_service", label: "Encontrei outra solução" },
  { key: "low_quality", label: "Tive problemas técnicos ou de qualidade" },
];

export default function CancelSubscriptionModal({ periodEnd, onClose, onDone }) {
  const [selected, setSelected] = useState([]);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const toggle = (k) => setSelected((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const submit = async (e) => {
    e.preventDefault();
    if (!selected.length || busy) return;
    setBusy(true);
    try {
      await cancelSubscription({ reasons: selected, comment });
      toast.success("Assinatura cancelada. Você mantém o acesso até o fim do período.");
      onDone && onDone();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(d?.message || (typeof d === "string" ? d : null) || "Não foi possível cancelar agora.");
      if (d?.code === "no_subscription") onDone && onDone();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] grid place-items-center p-4" data-testid="cancel-modal">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl ring-1 ring-slate-200 p-6 sm:p-7 float-up">
        <button type="button" onClick={onClose} data-testid="cancel-modal-close" className="absolute top-4 right-4 grid place-items-center w-8 h-8 rounded-full text-slate-400 hover:bg-slate-100 transition-colors">
          <X size={17} />
        </button>
        <h2 className="text-xl font-bold tracking-tight text-slate-900">Antes de ir, nos conte o motivo</h2>
        <p className="mt-1.5 text-[13.5px] text-slate-500 leading-relaxed">
          Sua resposta nos ajuda a melhorar o Basiq. Marque tudo o que se aplica.
        </p>

        <div className="mt-5 space-y-2" data-testid="cancel-reasons">
          {CANCEL_REASONS.map((r) => {
            const on = selected.includes(r.key);
            return (
              <button type="button" key={r.key} onClick={() => toggle(r.key)} data-testid={`cancel-reason-${r.key}`} aria-pressed={on}
                className={`w-full flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-left text-[13.5px] ring-1 transition-all ${on ? "ring-slate-900 bg-slate-50 text-slate-900" : "ring-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                <span className={`grid place-items-center w-5 h-5 rounded-md ring-1 shrink-0 transition-colors ${on ? "bg-slate-900 ring-slate-900 text-white" : "ring-slate-300 bg-white"}`}>
                  {on && <Check size={12} strokeWidth={3} />}
                </span>
                {r.label}
              </button>
            );
          })}
        </div>

        <textarea value={comment} onChange={(e) => setComment(e.target.value)} maxLength={1000} rows={3} data-testid="cancel-comment"
          placeholder="Quer contar mais? (opcional)"
          className="mt-4 w-full rounded-xl ring-1 ring-slate-200 focus:ring-2 focus:ring-slate-900 outline-none px-3.5 py-2.5 text-[13.5px] text-slate-800 placeholder:text-slate-400 resize-none transition-all" />

        {periodEnd && (
          <p className="mt-3 text-[12px] text-slate-500">Você continua com todos os recursos até <b className="text-slate-700">{periodEnd}</b>. Nenhuma cobrança adicional será feita.</p>
        )}

        <div className="mt-5 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <button type="button" onClick={onClose} data-testid="cancel-keep-btn" className="h-11 px-5 rounded-full bg-slate-900 text-white text-[13.5px] font-semibold whitespace-nowrap hover:bg-slate-800 transition-colors">
            Manter minha assinatura
          </button>
          <button type="submit" disabled={!selected.length || busy} data-testid="cancel-confirm-btn"
            className="h-11 px-5 rounded-full ring-1 ring-slate-200 text-[13.5px] font-medium text-slate-600 whitespace-nowrap hover:bg-slate-50 disabled:opacity-50 inline-flex items-center justify-center gap-2 transition-colors">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Confirmar cancelamento"}
          </button>
        </div>
      </form>
    </div>
  );
}
